import React, { useMemo } from 'react'
import _cx from 'classnames/bind'
import MarkdownRenderer from '../markdownRenderer'
import { parseSegments } from './parser'
import { builtInComponents } from './components'
import type { AutomationTarget } from '../../../types'
import type { MarkdownComponentAction, MarkdownComponentMap } from './types'
import cls from './styles.scss'

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const cx = _cx.bind(cls)

export interface MarkdownWithComponentsProps extends AutomationTarget {
  /** Raw markdown content, may contain ```ui:<name> blocks */
  content: string
  className?: string
  /** Additional or override component registry. Merged with built-ins. */
  components?: MarkdownComponentMap
  /** Callback fired when an interactive component triggers an action */
  onAction?: (action: MarkdownComponentAction) => void
}

const noop = () => {} // fallback when no onAction provided

/**
 * Renders markdown content with support for interactive ```ui:<component>``` blocks.
 *
 * Pre-processes the raw content into segments:
 * - Regular markdown → rendered by MarkdownRenderer
 * - ```ui:<name> blocks → rendered by registered components from the registry
 *
 * When content contains no ui: blocks, behaves identically to MarkdownRenderer.
 */
const MarkdownWithComponents: React.FC<MarkdownWithComponentsProps> = ({
  content,
  className,
  components: consumerComponents,
  onAction = noop,
  elmId = 'markdown-with-components',
}) => {
  // Merge built-in and consumer-provided component registries
  const registry = useMemo<MarkdownComponentMap>(
    () => ({ ...builtInComponents, ...consumerComponents }),
    [consumerComponents]
  )

  // Parse content into segments
  const segments = useMemo(() => parseSegments(content), [content])

  return (
    <div
      className={cx('markdown-with-components', className)}
      data-elm-id={elmId}
    >
      {segments.map((segment, index) => {
        if (segment.type === 'markdown') {
          return (
            <MarkdownRenderer
              key={`md-${index}`}
              content={segment.content}
              elmId={`${elmId}-markdown-${index}`}
            />
          )
        }

        // Component segment
        const Component = registry[segment.componentType]

        if (!Component) {
          // Unknown component type — render as a code block fallback
          return (
            <MarkdownRenderer
              key={`fallback-${index}`}
              content={`\`\`\`\nui:${segment.componentType}\n${JSON.stringify(
                segment.props,
                null,
                2
              )}\n\`\`\``}
              elmId={`${elmId}-fallback-${index}`}
            />
          )
        }

        return (
          <div key={`component-${index}`} className={cx('component-segment')}>
            <Component {...segment.props} onAction={onAction} />
          </div>
        )
      })}
    </div>
  )
}

export default React.memo(MarkdownWithComponents)
